/**
 * Inline string-field editor state for the firm detail panel (ADR-0091).
 *
 * Covers the four congruent clearable text fields — Notifications Email, Email
 * Domains, Address, Routing Preferences — which all share the same shape:
 *   - an `editing` open/closed flag,
 *   - a string `draft` buffer initialized from the firm's current value on
 *     start,
 *   - cancel resets the buffer to "",
 *   - save trims the draft and writes the trimmed string, or null when empty,
 *     then closes.
 *
 * What stays at the call site (genuinely divergent, not folded in here):
 *   - Name: rejects an empty draft instead of clearing-to-null, has no
 *     clear-confirm, and renders bespoke header chrome.
 *   - Designation: a two-step confirm flow over a non-string draft.
 *   - The per-field clear-confirm overlay flag, which is independent of the
 *     edit buffer.
 */

import { useState } from "react";

export type InlineEdit = {
  editing: boolean;
  draft: string;
  setDraft: (next: string) => void;
  /** Open the editor, seeding the draft from the field's current value.
   *  No-op when there's no wiring or a write is already in flight. */
  start: () => void;
  /** Trim the draft and write it (empty → null), then close. No-op when
   *  there's no wiring or a write is already in flight. */
  save: () => void;
  /** Close without writing, clearing the draft buffer. */
  cancel: () => void;
};

export function useInlineEdit(
  current: string | null,
  onUpdate: ((value: string | null) => void) | undefined,
  pending: boolean,
): InlineEdit {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  function start() {
    if (!onUpdate || pending) return;
    setDraft(current ?? "");
    setEditing(true);
  }

  function save() {
    if (!onUpdate || pending) return;
    const trimmed = draft.trim();
    onUpdate(trimmed.length > 0 ? trimmed : null);
    setEditing(false);
  }

  function cancel() {
    setEditing(false);
    setDraft("");
  }

  return { editing, draft, setDraft, start, save, cancel };
}
